import { Button, Dropdown, Input, Select, Space, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import CaretDownIcon from "../../../../assets/icons/CaretDownIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import ThreeDotsIcon from "../../../../assets/icons/ThreeDotsIcon";
import ActionModal from "../../../../components/ui/ActionModal";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  useGetAllFeaturesQuery,
  useUpdateFeatureStatusMutation,
} from "../../../../features/superAdmin/ticketService/feature/feature.api";
import { FeatureDetails } from "../../../../features/superAdmin/ticketService/feature/feature.types";
import { useGetAllProductsQuery } from "../../../../features/superAdmin/ticketService/product/product.api";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { selectSearchOption } from "../../../../utils/utils";
import ManageFeature from "./_components/ManageFeature";

const AllFeatures = () => {
  const {
    page,
    limit,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<{ product_id?: string; status: string }>({
    defaultFilter: { status: "all" },
  });
  const { open, handleOpen, handleClose } = useModal<unknown, "DELETE">();

  const { data, isLoading, isFetching, isError, error } =
    useGetAllFeaturesQuery({
      params: {
        page,
        limit,
        search: keyword,
        status: filter.status === "all" ? undefined : filter.status,
        product_id: filter.product_id as unknown as number,
      },
    });
  const { data: productData } = useGetAllProductsQuery({
    params: {
      page: 1,
      limit: 10000,
      status: "active",
    },
  });

  const productOptions = productData?.data?.products?.map((p) => ({
    value: p.id,
    label: p.title,
  }));

  // ------ Mutations ------
  const [
    updateStatus,
    {
      isLoading: updating,
      isError: isUpdateError,
      error: updateError,
      isSuccess,
    },
  ] = useUpdateFeatureStatusMutation();

  // ------ Effects ------
  useEffect(() => {
    if (!updating && isSuccess) {
      toast("success", "Feature status updated.");
    }
  }, [updating, isSuccess]);

  // ------ Errors ------
  useError(isError, error, "Feature get failed.");
  useError(isUpdateError, updateError, "Feature get failed.");

  const columns: ColumnsType<FeatureDetails> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      key: "product",
      dataIndex: "product",
      title: "Product Title",
      render: (val) => val?.title,
    },
    {
      key: "title",
      dataIndex: "title",
      title: "Feature Title",
    },
    {
      key: "description",
      dataIndex: "description",
      title: <span className="text-nowrap">Feature Description</span>,
      width: 400,
      render: (val) => (val ? <span className="w-[400px]">{val}</span> : "N/A"),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      align: "center" as const,
      render: (status: string) => (
        <div
          className={`status ${
            status.toUpperCase() === "ACTIVE"
              ? "status-success"
              : "status-danger"
          }`}
        >
          {capitalize(status)}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "center" as const,
      render: (data: any) => {
        const isActive = data?.status?.toUpperCase() === "ACTIVE";
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              overlayClassName="w-36"
              menu={{
                items: [
                  {
                    key: "0",
                    label: (
                      <Space className="flex items-center justify-between">
                        <span className="text-sm font-medium">Active</span>
                        <Switch size="default" checked={isActive} />
                      </Space>
                    ),
                    onClick: () => {
                      updateStatus({
                        id: data?.id,
                        data: { status: isActive ? "inactive" : "active" },
                      });
                    },
                  },
                  {
                    key: "2",
                    label: (
                      <Space className="flex items-center">
                        <EditIcon />
                        <span className="text-sm font-medium">Edit</span>
                      </Space>
                    ),
                    onClick: () => handleOpen({ type: "UPDATE", data: data }),
                  },
                  // {
                  //   key: "3",
                  //   label: (
                  //     <Space className="flex items-center">
                  //       <DeleteIcon />
                  //       <span className="text-sm font-medium">Delete</span>
                  //     </Space>
                  //   ),
                  //   onClick: () => handleOpen({ type: "DELETE", data: data }),
                  // },
                ],
              }}
              trigger={["click"]}
            >
              <div className="cursor-pointer">
                <ThreeDotsIcon />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <SidebarLayout title="Product Management">
      <CustomTable
        className="designationTable"
        limit={limit}
        page={page}
        columns={columns}
        dataSource={data?.data?.features}
        total={data?.data?.totalLength}
        loading={isLoading || isFetching}
        topSectionClassName="w-full"
        filterSection={
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2 items-center">
              <div>Total Rows</div>
              <Select
                placeholder="10"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 70 }}
                onChange={handleLimit}
                options={[
                  { label: "10", value: 10 },
                  { label: "20", value: 20 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 },
                  { label: "1000", value: 1000 },
                ]}
              />
              <div>of {data?.data?.totalLength}</div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by title"
                suffix={<SearchIcon />}
                size="middle"
                className="max-w-[200px]"
                onChange={handleSearch}
              />
              <Select
                allowClear
                showSearch
                placeholder="Product"
                suffixIcon={<CaretDownIcon />}
                filterOption={selectSearchOption}
                size="middle"
                style={{ width: 200 }}
                value={filter.product_id}
                onChange={(value) => handleFilter("product_id", value)}
                options={productOptions}
              />
              <Select
                allowClear
                placeholder="Status"
                suffixIcon={<CaretDownIcon />}
                size="middle"
                style={{ width: 120 }}
                value={filter.status}
                onChange={(value) => handleFilter("status", value)}
                options={[
                  { label: "All", value: "all" },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
              <Button
                className="button-primary-light h-9"
                onClick={() => handleOpen({ type: "ADD", data: undefined })}
              >
                <AddIcon />
                Add Feature
              </Button>
            </div>
          </div>
        }
      />

      <ManageFeature
        open={open.open && (open.type === "ADD" || open.type === "UPDATE")}
        data={open.data}
        handleClose={handleClose}
      />
      <ActionModal
        open={open?.open && open.type === "DELETE"}
        loading={false}
        title={"Do you want to delete this feature?"}
        subTitle={
          "Are you sure you want to delete this feature? This action cannot be."
        }
        iconType="danger"
        actionBtnText={`Yes, Delete`}
        actionBtnIcon
        actionBtnIconType="delete"
        actionBtnType="danger-light"
        cancelButton={false}
        handleClose={handleClose}
        handleAction={() => {}}
        handleCancel={handleClose}
      />
    </SidebarLayout>
  );
};

export default AllFeatures;
